import type { ContractDataFieldSectionDto } from "../../autogen/bff-api";
import { isCustomFieldTextSearchable } from "../contracts/customFieldFilters";
import type { CustomContractFieldFilter } from "./systemPrompt";

// listContractReports takes a single base64-encoded JSON query the OpenAPI spec can only describe
// as an opaque `q: string`. The agent catalog instead advertises the real filter params
// and this builds the actual query from them — so the model never has to
// guess the encoding, and an unrecognized shape can't silently forward a filter the BFF ignores
// (which would return every contract unfiltered instead of the one searched for).
const CONTRACT_LIST_FILTER_KEYS = [
  "titleFilter",
  "statuses",
  "state",
  "counterpartyName",
  "counterpartyContactFullName",
  "counterpartyContactEmail",
  "counterpartyContactPhoneNumber",
  "projectSearchTerm",
  "internalOwnerSearchTerm",
  "internalPartySearchTerm",
  "descriptionSearchTerm",
  "referenceNumberSearchTerm",
  "templateNameSearchTerm",
  "originSourcingEventName",
  "linkedContractTitleSearchTerm",
  "publishedBySearchTerm",
  "terminatedBySearchTerm",
  "riskLevels",
  "priorityLevels",
  "supplierCategories",
  "frameworkRoles",
  "currencies",
  "startDateFrom",
  "startDateTo",
  "endDateFrom",
  "endDateTo",
  "renewalDateFrom",
  "renewalDateTo",
  "terminationDateFrom",
  "terminationDateTo",
  "createdAtFrom",
  "createdAtTo",
  "pricePerMonthFrom",
  "pricePerMonthTo",
  "pricePerYearFrom",
  "pricePerYearTo",
  "totalLiabilityFrom",
  "totalLiabilityTo",
  "estimatedValueFrom",
  "estimatedValueTo",
  "totalPriceFrom",
  "totalPriceTo",
  "customFieldSearch",
  "customFieldSelect",
] as const;

const CONTRACT_REPORT_FILTER_KEYS = [...CONTRACT_LIST_FILTER_KEYS, "limit", "skip"] as const;

const pickKnownFilters = (raw: Record<string, unknown>): Record<string, unknown> => {
  const picked: Record<string, unknown> = {};
  for (const key of CONTRACT_REPORT_FILTER_KEYS) {
    if (raw[key] !== undefined) {
      picked[key] = raw[key];
    }
  }
  return picked;
};

const collectQueryFields = (args: Record<string, unknown>): Record<string, unknown> => {
  const q = args.q;
  let raw: Record<string, unknown>;
  if (typeof q === "string") {
    const trimmed = q.trim();
    if (trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        raw = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
      } catch {
        raw = { titleFilter: trimmed };
      }
    } else {
      raw = trimmed ? { titleFilter: trimmed } : {};
    }
  } else if (q && typeof q === "object") {
    raw = q as Record<string, unknown>;
  } else {
    raw = { ...args };
    delete raw.q;
  }

  const direct = pickKnownFilters(raw);
  if (Object.keys(direct).length > 0) {
    return direct;
  }
  for (const value of Object.values(raw)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nested = pickKnownFilters(value as Record<string, unknown>);
      if (Object.keys(nested).length > 0) {
        return nested;
      }
    }
  }
  return {};
};

export const buildContractReportQuery = (
  args: Record<string, unknown>,
  organizationId: string,
): Record<string, unknown> => ({
  limit: 25,
  skip: 0,
  ...collectQueryFields(args),
  owningOrganizationId: organizationId,
});

export const contractListFilters = (args: Record<string, unknown>): Record<string, unknown> => {
  const fields = collectQueryFields(args);
  const filters: Record<string, unknown> = {};
  for (const key of CONTRACT_LIST_FILTER_KEYS) {
    if (fields[key] !== undefined) filters[key] = fields[key];
  }
  return filters;
};

export const filterableCustomContractFields = (
  sections: Record<string, ContractDataFieldSectionDto> | null | undefined,
): CustomContractFieldFilter[] =>
  Object.values(sections ?? {})
    .flatMap((section) => section.fields)
    .filter((field) => field.type === "CustomBoolean" || isCustomFieldTextSearchable(field.type))
    .map((field) => ({ id: field.id, name: field.name, yesNo: field.type === "CustomBoolean" }));
